'use client';

import Link from 'next/link';
import Image from 'next/image';
import { MapPin, Award, Heart, Sparkles } from 'lucide-react';
import { PROJECT_NAME } from '@shared/constants';
import { BrandPromiseSection, TrustGovernanceSection } from './about/sections';
import { PublicSection } from '../../components/public/PublicSection';
import { LandingPageCard } from './landing/components';

/**
 * @group Public
 * @summary About page component
 * 
 * Displays information about Phila's mission, values, and team.
 */
export function AboutPage() {
  const values = [
    {
      icon: MapPin,
      title: 'Access for All',
      description: 'Healthcare should be accessible to everyone—from city centres to rural villages. We\'re committed to reaching underserved communities across South Africa.',
    },
    {
      icon: Award,
      title: 'Clinical Excellence',
      description: 'Every doctor on our platform is HPCSA-verified. We maintain the highest standards of clinical governance and patient safety.',
    },
    {
      icon: Heart,
      title: 'Cultural Sensitivity',
      description: 'We understand that healthcare works best when delivered with cultural awareness and respect for diverse backgrounds and experiences.',
    },
    {
      icon: Sparkles,
      title: 'Innovation with Integrity',
      description: 'We use AI to support clinical decisions, never replace them. Technology enhances our doctors\' capabilities while preserving their autonomy.',
    },
  ];

  return (
    <main className="min-h-screen">
      {/* Hero Section - INDENTED - LIGHT */}
      <PublicSection
        heading={`About ${PROJECT_NAME}`}
        subheading={`"Phila" means "to live" or "to be well". We're building healthcare that reflects our nation's diversity.`}
        headingColor="#084349"
        subheadingColor="#084349"
        separatorColor="#084349"
        separatorLineColor="hsl(var(--brand-teal) / 0.4)"
        className="bg-hero-bg"
        indented
      >
        <div className="grid gap-4 md:grid-cols-12">
          <div className="relative h-56 overflow-hidden rounded-2xl md:col-span-8 md:h-72">
            <Image
              src="/img/about-team-collaboration.jpg"
              alt="Team collaborating to improve healthcare access"
              fill
              className="object-cover object-center"
              priority
            />
          </div>
          <div className="relative h-56 overflow-hidden rounded-2xl md:col-span-4 md:h-72">
            <Image
              src="/img/about-mobile-telehealth-use.png"
              alt="Patient-centered care supported by Phila"
              fill
              className="object-cover object-center"
            />
          </div>
          <div className="relative h-64 overflow-hidden rounded-2xl md:col-span-12 md:h-[28rem]">
            <Image
              src="/img/about-patient-care-smile.png"
              alt="Healthcare team celebrating improved access outcomes"
              fill
              className="object-cover object-top"
            />
          </div>
        </div>
      </PublicSection>

      {/* Mission Section - POKING OUT - DARK */}
      <PublicSection 
        heading="Our Mission"
        headingColor="hsl(var(--brand-mint))"
        subheadingColor="hsl(var(--brand-mint))"
        separatorColor="hsl(var(--brand-mint))"
        separatorLineColor="hsl(var(--brand-mint) / 0.4)"
        className="bg-brand-teal"
        indented={false}
      >
        <div className="prose prose-lg max-w-3xl mx-auto text-white/90 text-center">
          <p className="mb-4 leading-relaxed">
            {PROJECT_NAME} was founded with a simple but powerful mission: to bring quality healthcare 
            to every South African, regardless of where they live.
          </p>
          <p className="leading-relaxed">
            We believe that access to healthcare shouldn't depend on your proximity to a city 
            or your ability to take time off work. Through telemedicine, we're removing barriers 
            that have kept millions from receiving the care they deserve.
          </p>
        </div>
      </PublicSection>

      {/* Values Section - INDENTED - MINT */}
      <PublicSection 
        heading="Our Values"
        subheading="These core principles guide everything we do and every decision we make."
        headingColor="#084349"
        subheadingColor="#084349"
        separatorColor="#084349"
        separatorLineColor="hsl(var(--brand-teal) / 0.4)"
        className="bg-brand-mint"
        indented
      >
        <div className="grid md:grid-cols-2 gap-8">
          {values.map((value, idx) => (
            <LandingPageCard
              key={idx}
              icon={value.icon}
              title={value.title}
              description={value.description}
              iconBg="bg-secondary"
              className="p-8"
            />
          ))}
        </div>
      </PublicSection>

      {/* Brand Promise Section - POKING OUT */}
      <BrandPromiseSection />

      {/* Trust & Governance Section - INDENTED (inverted) */}
      <TrustGovernanceSection />

      {/* CTA Section - POKING OUT - MINT */}
      <PublicSection 
        heading={`Ready to Experience ${PROJECT_NAME}?`}
        subheading="Join thousands of South Africans who have discovered a better way to access healthcare."
        headingColor="#084349"
        subheadingColor="#084349"
        separatorColor="#084349"
        separatorLineColor="hsl(var(--brand-teal) / 0.4)"
        className="bg-brand-mint"
        indented={false}
      >
        <div className="text-center">
          <Link
            href="/download"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
          >
            Get Started Today
          </Link>
        </div>
      </PublicSection>
    </main>
  );
}
